import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShoppingCart, FileCheck, Truck, Beaker, Snowflake, MapPin, BellRing, ShieldCheck, Lock, BadgeCheck, Calendar, FlaskConical, Gift } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../lib/cart";
import { B3G1_SKUS, retaPromo, useB3G1Strengths, joinStrengths } from "../lib/promo";
import { useBactPrompt } from "../lib/bactPrompt";
import { track } from "../lib/gtag";
import { metaTrack } from "../lib/meta";
import { toast } from "sonner";
import ProductVial from "../components/ProductVial";
import QuantityTierTable from "../components/QuantityTierTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { resolveTierPrice } from "../lib/pricing";
import Reviews from "../components/Reviews";
import BactWaterUpsell from "../components/BactWaterUpsell";
import RecommendedProducts from "../components/RecommendedProducts";
import LiveStockBadge from "../components/LiveStockBadge";
import CountdownTimer from "../components/CountdownTimer";
import useLiveStock from "../lib/useLiveStock";
import { ImageDisclaimer } from "../components/ImageDisclaimer";
import usePageMeta from "../lib/usePageMeta";

export default function ProductDetail() {
  const { sku } = useParams();
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [qty, setQty] = useState(1);
  const [notifyEmail, setNotifyEmail] = useState("");
  const { items, addItem } = useCart();
  const { promptAndAdd } = useBactPrompt();
  const liveStock = useLiveStock();
  const b3g1Strengths = useB3G1Strengths();
  usePageMeta(
    product ? `${product.name} ${product.strength} | UK Research Peptide Supplier | Peptigen Labs` : "Research Peptide",
    product ? `${product.name} ${product.strength} — research-use-only laboratory material. Independently purity tested. UK stock, same-day dispatch for qualified researchers.` : "Lyophilized research peptide, UK stock, research use only.",
    product?.image_url || undefined
  );

  // Build the COA download URL.
  //
  // Strategy: use a SAME-ORIGIN URL whenever the page itself is being
  // served from a real host (e.g. peptigenlabs.co.uk). The Emergent
  // backend is reverse-proxied at /api/* on the customer's domain, so
  // `${origin}/api/files/...` resolves to the same FastAPI handler as
  // `REACT_APP_BACKEND_URL/api/files/...` — but with three big wins:
  //   1. No CORS — browsers respect `<a download href>` natively
  //      and the `Content-Disposition: attachment` header from a
  //      same-origin response always triggers a download.
  //   2. Sane edge-cache TTL — Cloudflare honours the backend's
  //      `max-age=60` on the customer's domain (vs. Emergent's CDN
  //      pinning 4h cache on the `emergent.host` mirror).
  //   3. Survives popup blockers — there is no JS click handler that
  //      could be eaten by a blocker; the click is a real anchor nav
  //      that the browser converts to a download because of the
  //      response header. Works on iOS Safari, Android Chrome, every
  //      desktop browser.
  //
  // SSR safety: `window` is only read inside the render function which
  // doesn't execute during static-site builds (CRA renders on the
  // browser only). On the off-chance window is undefined we fall back
  // to the REACT_APP_BACKEND_URL flavour.
  const coaHref = (() => {
    if (!product?.coa_filename) return "";
    const filename = product.coa_filename;
    const cacheBust = `?v=${encodeURIComponent(filename)}`;
    try {
      if (typeof window !== "undefined" && window.location?.origin) {
        return `${window.location.origin}/api/files/${filename}${cacheBust}`;
      }
    } catch (_) { /* fall through */ }
    return `${API}/files/${filename}${cacheBust}`;
  })();

  useEffect(() => {
    api.get(`/products/${sku}`).then((r) => {
      setProduct(r.data);
      track.viewItem(r.data);
      metaTrack.viewItem(r.data);
    }).catch(() => setProduct(false));
  }, [sku]);

  // Load sibling strength variants once the current product is known.
  // Matches by product.name so the picker auto-populates for every
  // multi-strength line (Retatrutide 10/20/30, GHK-Cu 50/100,
  // Bacteriostatic Water 3/10 ml, Semax 5/10 mg, etc.) without needing
  // a per-product config.
  useEffect(() => {
    if (!product?.name) {
      setVariants([]);
      return;
    }
    let alive = true;
    api.get("/products").then((r) => {
      if (!alive) return;
      const siblings = (r.data || [])
        .filter((p) => p.name === product.name && p.sku !== product.sku)
        .sort((a, b) => (parseFloat(a.strength) || 0) - (parseFloat(b.strength) || 0));
      setVariants(siblings);
    }).catch(() => alive && setVariants([]));
    return () => { alive = false; };
  }, [product?.name, product?.sku]);

  if (product === null) return <div className="max-w-7xl mx-auto py-20 px-6 text-[#9CA8B8] pg-mono">Loading...</div>;
  if (product === false) {
    return (
      <div className="max-w-7xl mx-auto py-20 px-6">
        <h1 className="text-[#F7FAFC] text-3xl pg-display mb-4">Product not found</h1>
        <Link to="/products" className="pg-btn-ghost">Back to catalogue</Link>
      </div>
    );
  }

  const live = liveStock[product.sku];
  const liveQty = live?.stock_quantity ?? product.stock_quantity;
  const liveStatus = live?.stock_status ?? product.stock_status;
  const inStock = liveStatus !== "out_of_stock" && liveQty > 0;
  const isReagent = product.category === "Laboratory Reagents";
  const hasPeptide = items.some((i) => i.sku !== "PL-BACT-3");

  const handleAdd = () => {
    promptAndAdd(product, qty);
  };

  const handleNotify = async (e) => {
    e.preventDefault();
    try {
      await api.post("/notify", { product_id: product.id, email: notifyEmail });
      toast.success("We'll notify you when stock is available");
      setNotifyEmail("");
    } catch {
      toast.error("Could not save your request");
    }
  };

  const spec = [
    ["Product", product.name],
    ["Quantity", product.strength],
    ["Form", product.form],
    ["Appearance", product.appearance],
    ["Purity", product.purity || "≥99%"],
    ["Storage", product.storage],
    ["Stock location", product.stock_location],
    ["Intended use", product.intended_use],
    ["SKU", product.sku],
    ["Batch", product.batch_number || "Available on request"],
    ["COA", "Available on request"],
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid={`product-detail-${product.sku}`}>
      <div className="mb-6 text-xs pg-mono text-[#9CA8B8] tracking-[0.2em]">
        <Link to="/products" className="hover:text-[#00C2D6]">CATALOGUE</Link>
        <span className="mx-2">/</span>
        <span className="text-[#00C2D6]">{product.category.toUpperCase()}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="pg-card aspect-square relative overflow-hidden">
            <ProductVial product={product} size="lg" outOfStock={!inStock} />
          </div>
          <div className="mt-3">
            <ImageDisclaimer compact />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="pg-card p-3 text-center">
              <ShieldCheck size={14} className="text-[#5EEAD4] mx-auto mb-1" />
              <div className="pg-micro">Purity</div>
              <div className="text-sm text-[#F7FAFC] pg-mono">{product.purity || "≥99%"}</div>
            </div>
            <div className="pg-card p-3 text-center">
              <Beaker size={14} className="text-[#00C2D6] mx-auto mb-1" />
              <div className="pg-micro">Form</div>
              <div className="text-[10px] text-[#F7FAFC]">{product.form === "Sterile solution" ? "Solution" : "Powder"}</div>
            </div>
            <div className="pg-card p-3 text-center">
              <Snowflake size={14} className="text-[#00C2D6] mx-auto mb-1" />
              <div className="pg-micro">Storage</div>
              <div className="text-[10px] text-[#F7FAFC]">{product.storage}</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="flex flex-wrap gap-2 mb-4">
            {inStock ? (
              <>
                <span className="pg-badge-stock" data-testid="detail-instock-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#A6FF00]" /> In Stock
                </span>
                <span className="pg-badge-cyan">UK Stock</span>
                <span className="pg-badge-cyan">Same-Day Dispatch</span>
                <span className="pg-badge-cyan">Next-Day Delivery</span>
              </>
            ) : (
              <span className="pg-badge-out">Out of Stock</span>
            )}
            <span className="pg-badge-cyan">{product.purity || "≥99% Purity"}</span>
            <span className="pg-badge-cyan">Research Use Only</span>
          </div>

          <h1 className="text-[#F7FAFC] text-4xl lg:text-5xl pg-display" style={{ fontFamily: "'Exo 2'" }} data-testid="product-name">
            {product.name} {product.strength}
          </h1>
          <p className="text-[#9CA8B8] mt-2">
            {isReagent
              ? "Sterile reagent. UK stock. Research use only."
              : "Lyophilised research peptide. Independently purity tested. UK stock. Research use only."}
          </p>

          {variants.length > 0 && (() => {
            // Compute £/mg (or £/ml for reagents) for each option to
            // tag the best-value variant. Falls back gracefully if the
            // strength field can't be parsed numerically.
            const all = [product, ...variants];
            const perUnit = all.map((p) => {
              const n = parseFloat(p.strength) || 0;
              return n > 0 ? p.price / n : Number.POSITIVE_INFINITY;
            });
            const bestPerUnit = Math.min(...perUnit);
            const bestSku = all[perUnit.indexOf(bestPerUnit)]?.sku;
            const unitLabel = (product.strength || "").toLowerCase().includes("ml") ? "ml" : "mg";

            return (
              <div className="mt-6" data-testid="strength-variant-picker">
                <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#5EEAD4] mb-3">
                  Also available — bigger sizes, better value
                </div>
                <div className="flex flex-wrap gap-2">
                  {all.map((p) => {
                    const isCurrent = p.sku === product.sku;
                    const isBest = p.sku === bestSku;
                    const n = parseFloat(p.strength) || 0;
                    const per = n > 0 ? (p.price / n).toFixed(2) : null;
                    const baseClasses = "px-4 py-3 rounded transition-all relative";
                    const stateClasses = isCurrent
                      ? "border-2 border-[#A6FF00] bg-[#A6FF00]/10"
                      : "border border-[#1E2A44] bg-[#0B1322] hover:border-[#5EEAD4] hover:bg-[#0F1A2E]";
                    const inner = (
                      <>
                        {isBest && !isCurrent && (
                          <div className="absolute -top-2 left-3 px-1.5 py-0.5 bg-[#A6FF00] text-[#020611] pg-mono text-[8px] font-bold tracking-[0.14em] uppercase rounded-sm">
                            Best value
                          </div>
                        )}
                        <div className="text-[#F7FAFC] font-bold text-base leading-tight" style={{ fontFamily: "'Exo 2'" }}>
                          {p.strength}
                        </div>
                        <div className="mt-1 flex items-baseline gap-1.5">
                          <span className={`pg-mono text-sm font-semibold ${p.on_sale ? "text-[#A6FF00]" : "text-[#F7FAFC]"}`}>
                            £{p.price.toFixed(2)}
                          </span>
                          {p.compare_at_price && p.on_sale && (
                            <span className="pg-mono text-[10px] text-[#9CA8B8] line-through">
                              £{p.compare_at_price.toFixed(2)}
                            </span>
                          )}
                        </div>
                        {per && (
                          <div className={`pg-mono text-[10px] mt-0.5 ${isBest ? "text-[#A6FF00]" : "text-[#9CA8B8]"}`}>
                            £{per}/{unitLabel}
                          </div>
                        )}
                        {isCurrent && (
                          <div className="pg-mono text-[9px] uppercase tracking-[0.18em] text-[#A6FF00] mt-1 font-bold">
                            Current
                          </div>
                        )}
                      </>
                    );
                    return isCurrent ? (
                      <div
                        key={p.sku}
                        className={`${baseClasses} ${stateClasses} cursor-default`}
                        data-testid={`variant-current-${p.sku}`}
                      >
                        {inner}
                      </div>
                    ) : (
                      <Link
                        key={p.sku}
                        to={`/products/${p.sku}`}
                        className={`${baseClasses} ${stateClasses} group`}
                        data-testid={`variant-link-${p.sku}`}
                      >
                        {inner}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div className="mt-8 pg-card p-6">
            {product.on_sale && product.sale_ends_at && (
              <div
                className="mb-5 -mx-6 -mt-6 px-6 py-4 border-b-2 border-[#A6FF00]/40"
                style={{ background: "linear-gradient(90deg, rgba(166,255,0,0.10), rgba(0,194,214,0.04))" }}
                data-testid="pdp-sale-countdown"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <div className="pg-mono text-[10px] uppercase tracking-[0.28em] text-[#A6FF00] font-bold mb-1">
                      Flash Sale · Ends Soon
                    </div>
                    <div className="text-[#9CA8B8] text-xs">
                      Price auto-reverts to <span className="text-[#F7FAFC] font-bold">£{product.compare_at_price?.toFixed(2) || "79.00"}</span> when the timer hits zero.
                    </div>
                  </div>
                  <CountdownTimer targetIso={product.sale_ends_at} compact />
                </div>
              </div>
            )}
            <div className="flex items-end justify-between mb-5">
              <div>
                <div className="pg-micro mb-1">
                  {product.on_sale
                    ? "Special Offer Price"
                    : product.price_tiers?.length
                      ? `Single-Vial Price (${qty} × £${resolveTierPrice(product, qty).toFixed(2)})`
                      : "Price (Research-Use-Only)"}
                </div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <div className={`text-4xl ${product.on_sale || (!product.on_sale && product.price_tiers && qty > 1) ? "text-[#A6FF00]" : "text-[#F7FAFC]"}`} style={{ fontFamily: "'Exo 2'", fontWeight: 800 }} data-testid="product-price">
                    £{resolveTierPrice(product, qty).toFixed(2)}
                  </div>
                  {product.compare_at_price && product.on_sale && (
                    <>
                      <div className="text-[#9CA8B8] text-xl line-through pg-mono" data-testid="product-compare-price">
                        £{product.compare_at_price.toFixed(2)}
                      </div>
                      <span
                        className="pg-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-sm font-bold"
                        style={{ background: "#A6FF00", color: "#0B1220" }}
                      >
                        Save £{(product.compare_at_price - product.price).toFixed(2)}
                      </span>
                    </>
                  )}
                  {!product.on_sale && product.price_tiers?.length > 0 && qty > 1 && (
                    <span
                      className="pg-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-sm font-bold"
                      style={{ background: "#A6FF00", color: "#0B1220" }}
                    >
                      Save £{((product.price - resolveTierPrice(product, qty)) * qty).toFixed(2)}
                    </span>
                  )}
                </div>
                {product.bundle_price && (
                  <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#A6FF00] mt-1">
                    Bundle price £{product.bundle_price.toFixed(2)} when ordered with any peptide
                  </div>
                )}
              </div>
              {inStock && (
                <div className="flex items-center gap-2">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 border border-[#00C2D6]/30 text-[#F7FAFC] hover:border-[#00C2D6]" data-testid="qty-decrease">−</button>
                  <input type="number" min="1" value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1")))} className="w-14 h-10 text-center pg-input" data-testid="qty-input" />
                  <button onClick={() => setQty(qty + 1)} className="w-10 h-10 border border-[#00C2D6]/30 text-[#F7FAFC] hover:border-[#00C2D6]" data-testid="qty-increase">+</button>
                </div>
              )}
            </div>

            {B3G1_SKUS.includes(product.sku) && inStock && (() => {
              // Live progress toward the free vial, factoring what's already
              // in the basket plus the qty currently selected here.
              const projected = retaPromo([
                ...items,
                { sku: product.sku, price: product.price, quantity: qty },
              ]);
              const toNext = projected.remainder === 0 ? 0 : 4 - projected.remainder;
              return (
                <div
                  className="mt-5 rounded-md border border-[#00C2D6]/45 overflow-hidden"
                  data-testid="detail-b3g1-offer"
                >
                  <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: "linear-gradient(90deg, rgba(0,194,214,0.18), rgba(166,255,0,0.10))" }}>
                    <Gift size={16} className="text-[#00C2D6]" />
                    <span className="pg-mono text-[11px] uppercase tracking-[0.2em] text-[#00C2D6] font-bold">
                      Buy 3 Get 1 Free
                    </span>
                  </div>
                  <div className="px-4 py-3 bg-[#00C2D6]/[0.05] text-[13px] leading-[1.6] text-[#C7D2DE]">
                    Buy any <strong className="text-[#F7FAFC]">4 Retatrutide vials</strong> and the
                    cheapest is <strong className="text-[#A6FF00]">free</strong>
                    {b3g1Strengths.length > 1 && (
                      <>
                        {" "}— mix &amp; match{" "}
                        <strong className="text-[#F7FAFC]" data-testid="detail-b3g1-strengths">{joinStrengths(b3g1Strengths)}</strong>
                      </>
                    )}
                    . No code needed; applied
                    automatically at checkout.
                    {projected.freeCount > 0 ? (
                      <div className="mt-2 text-[#A6FF00] font-semibold" data-testid="detail-b3g1-status">
                        ✓ You’ll get {projected.freeCount} vial{projected.freeCount > 1 ? "s" : ""} free with this basket.
                      </div>
                    ) : (
                      <div className="mt-2 text-[#00C2D6] font-semibold" data-testid="detail-b3g1-status">
                        Add {toNext} more to unlock a free vial.
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {inStock ? (
              <button onClick={handleAdd} className="pg-btn-primary w-full mt-5" data-testid="add-to-basket-btn">
                <ShoppingCart size={18} /> Add to Basket
              </button>
            ) : (
              <form onSubmit={handleNotify} className="flex gap-2">
                <input type="email" required placeholder="Notify me when in stock" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} className="pg-input flex-1" data-testid="detail-notify-email" />
                <button type="submit" className="pg-btn-primary" data-testid="detail-notify-submit">
                  <BellRing size={16} /> Notify
                </button>
              </form>
            )}

            <div className="mt-4 flex items-center justify-center gap-3 flex-wrap" data-testid="pdp-live-stock-row">
              <LiveStockBadge qty={liveQty} status={liveStatus} testid={`pdp-live-stock-${product.sku}`} />
              {inStock && liveQty <= 10 && (
                <span className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#FF8A5B]">
                  · Selling fast
                </span>
              )}
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-[#9CA8B8]">
              <div className="flex items-center gap-2 p-3 border border-[#A6FF00]/20 bg-[#A6FF00]/5 rounded-sm">
                <Truck size={14} className="text-[#A6FF00]" />
                Order before 14:00 — next-day UK delivery
              </div>
              <div className="flex items-center gap-2 p-3 border border-[#00C2D6]/20 bg-[#00C2D6]/5 rounded-sm">
                <Lock size={14} className="text-[#00C2D6]" />
                Secure encrypted UK checkout
              </div>
            </div>

            <QuantityTierTable product={product} qty={qty} />
          </div>

          {!isReagent && inStock && (
            <div className="mt-5">
              <div className="pg-micro mb-3">// Recommended add-on</div>
              <BactWaterUpsell items={items} addItem={addItem} hasPeptide={true} />
            </div>
          )}

          <div className="mt-6 p-5 border border-[#A6FF00]/30 bg-[#A6FF00]/[0.04]" data-testid="rou-disclaimer">
            <div className="pg-micro text-[#A6FF00] mb-1">Compliance</div>
            <p className="text-xs text-[#9CA8B8] leading-relaxed">
              For laboratory research use only. Not for human consumption. Not intended for
              diagnostic, therapeutic, veterinary, or clinical use.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title="Overview">{product.overview || "Supplied as a lyophilised research peptide for use in qualified laboratory research environments. Strength, form and storage are presented on each batch label."}</Section>
            <Section title="Research Context">{product.research_context || "Listed in the Peptigen Labs catalogue strictly as research material. We do not provide advice on human use, dosage, administration or therapeutic outcomes."}</Section>
          </div>

          <div className="mt-8">
            <h2 className="pg-display text-2xl mb-4 text-[#F7FAFC]" style={{ fontFamily: "'Exo 2'" }}>Specification</h2>
            <div className="pg-card divide-y divide-[#00C2D6]/10">
              {spec.map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 px-5 py-3">
                  <div className="pg-micro col-span-1">{k}</div>
                  <div className="text-[#F7FAFC] col-span-2 text-sm">{v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Lab Report / COA */}
          <div className="mt-8 pg-card p-6" data-testid="coa-section">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="pg-micro mb-1">// Lab Report</div>
                <h3 className="text-[#F7FAFC] text-xl pg-heading mb-1" style={{ fontFamily: "'Rajdhani'" }}>Certificate of Analysis (COA)</h3>
                <p className="text-[#9CA8B8] text-sm leading-relaxed">
                  {product.coa_filename
                    ? "Independently tested by Brown Institute of Biomolecular Research. Download the Certificate of Analysis for this batch below."
                    : "Every batch is independently purity-tested. Each Certificate of Analysis is available on request — please contact our UK team quoting your batch number and we will issue the report to you directly."}
                </p>
              </div>
              <FileCheck size={28} className="text-[#A6FF00] shrink-0" />
            </div>

            {/* Independent-lab trust block — only when a COA is on file.
                Reinforces authenticity at the highest-intent moment on the
                product page. Composed of:
                  - A seal-style "Verified" badge (lime accent)
                  - The independent lab name + batch reference
                  - The reproduction (test) date in a Calendar chip
                  - The purity result from the product spec
                Pure HTML/CSS, no images required, brand-on. */}
            {product.coa_filename && (
              <div
                className="mt-5 grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-5 p-5 rounded-sm border border-[#A6FF00]/30 bg-[#A6FF00]/[0.04]"
                data-testid="coa-trust-block"
              >
                {/* Verified seal — pseudo-stamp built with CSS rings */}
                <div className="flex sm:flex-col items-center gap-3 sm:gap-2 sm:pr-5 sm:border-r sm:border-[#A6FF00]/20">
                  <div className="relative flex items-center justify-center w-[68px] h-[68px]" aria-hidden="true">
                    <div className="absolute inset-0 rounded-full border-2 border-[#A6FF00]/60" />
                    <div className="absolute inset-[5px] rounded-full border border-dashed border-[#A6FF00]/40" />
                    <BadgeCheck size={32} className="text-[#A6FF00] relative z-10" strokeWidth={2.2} />
                  </div>
                  <div className="sm:text-center">
                    <div className="text-[#A6FF00] pg-mono text-[10px] tracking-[0.22em] uppercase">Verified</div>
                    <div className="text-[#9CA8B8] pg-mono text-[9px] tracking-[0.18em] uppercase sm:mt-0.5">Independent Lab</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div data-testid="coa-trust-lab">
                    <div className="flex items-center gap-1.5 text-[#5EEAD4] pg-mono text-[10px] tracking-[0.18em] uppercase mb-1">
                      <FlaskConical size={12} /> Tested by
                    </div>
                    <div className="text-[#F7FAFC] text-sm leading-snug">
                      Brown Institute of<br />Biomolecular Research
                    </div>
                  </div>
                  <div data-testid="coa-trust-batch">
                    <div className="flex items-center gap-1.5 text-[#5EEAD4] pg-mono text-[10px] tracking-[0.18em] uppercase mb-1">
                      <ShieldCheck size={12} /> Batch
                    </div>
                    <div className="text-[#F7FAFC] text-sm pg-mono">
                      {product.batch_number || "Per shipment"}
                    </div>
                    {product.purity && (
                      <div className="text-[#A6FF00] text-xs mt-1 pg-mono">{product.purity} pure</div>
                    )}
                  </div>
                  <div data-testid="coa-trust-date">
                    <div className="flex items-center gap-1.5 text-[#5EEAD4] pg-mono text-[10px] tracking-[0.18em] uppercase mb-1">
                      <Calendar size={12} /> Tested
                    </div>
                    <div className="text-[#F7FAFC] text-sm">
                      {product.coa_tested_date || "See COA"}
                    </div>
                    {product.expiry_date && (
                      <div className="text-[#9CA8B8] text-xs mt-1">
                        Valid to {product.expiry_date}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-5 flex flex-col sm:flex-row gap-3 items-start">
              {product.coa_filename ? (
                <a
                  href={coaHref}
                  download={product.coa_filename}
                  target="_blank"
                  rel="noopener"
                  className="pg-btn-primary inline-flex items-center gap-2"
                  data-testid="download-coa-btn"
                >
                  <FileCheck size={16} />
                  Download Certificate of Analysis
                </a>
              ) : (
                <>
                  <span className="pg-badge-cyan" data-testid="coa-available-on-request">COA Available on Request</span>
                  <Link to="/contact" className="pg-btn-ghost" data-testid="request-coa">Request Lab Report</Link>
                </>
              )}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <InfoTile icon={Beaker} title="Storage" body={`Store at ${product.storage}. Reconstitution is the responsibility of the qualified researcher.`} />
            <InfoTile icon={Snowflake} title="Shipping" body="Sealed, padded UK packaging. Lyophilised — stable for next-day transit. Refrigerate on receipt." />
            <InfoTile icon={MapPin} title="UK Stock" body="Held in UK stock and dispatched from a British facility." />
          </div>

          {/* Image authenticity full notice */}
          <div className="mt-8">
            <ImageDisclaimer />
          </div>
        </div>
      </div>

      {/* Reviews specifically for this product */}
      <Reviews limit={3} productName={product.name} heading={true} />

      {/* Cross-sell: other peptides */}
      <RecommendedProducts
        currentSku={product.sku}
        category={product.category}
        title="Researchers also added"
        subtitle="Other research-use-only peptides held in UK stock, dispatched the same working day."
        testid="pdp-recommended"
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="pg-card p-5">
      <div className="pg-micro mb-2">// {title}</div>
      <p className="text-[#9CA8B8] text-sm leading-relaxed">{children}</p>
    </div>
  );
}

function InfoTile({ icon: Icon, title, body }) {
  return (
    <div className="pg-card p-5">
      <Icon size={18} className="text-[#00C2D6] mb-3" />
      <div className="text-[#F7FAFC] pg-heading text-base mb-1" style={{ fontFamily: "'Rajdhani'" }}>{title}</div>
      <div className="text-[#9CA8B8] text-xs leading-relaxed">{body}</div>
    </div>
  );
}
